import React, {
  forwardRef,
  ForwardRefExoticComponent,
  JSX,
  Ref,
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import dayjs from 'dayjs';
import { useTheme } from '@emotion/react';
import ReactDatePicker from 'react-datepicker';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import ReactDatePickerRenderer from './ReactDatePickerRenderer';
import { OnChangeDate, OnChangeDateIntersection } from './CustomDisplayProps';
import DatePickerInModal, {
  CustomsProps,
  DatePickerOnChange,
  DatePickerProps
} from './DatePickerInModal';
import DataTestIdProp from '../DataTestIdProp';
import { useAppService, useConfig } from '@ibe/components';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

export interface DatePickerRef {
  openDatePicker: () => void;
  closeDatePicker: () => void;
  forceUpdate: () => void;
}

export interface DateRangePickerRef {
  togglePopover: () => void;
  openPopover: () => void;
  closePopover: () => [startDate?: string, endDate?: string];
  getCurrentDateRange: () => [startDate?: string, endDate?: string];
}

const DatePicker: ForwardRefExoticComponent<
  DatePickerProps &
    DataTestIdProp &
    CustomsProps & {
      ref?: Ref<DatePickerRef>;
      onChange: DatePickerOnChange;
    }
> = forwardRef(
  (props, ref: Ref<DatePickerRef>): JSX.Element => {
    const theme = useTheme();
    const config = useConfig();
    const appService = useAppService();

    const {
      placeholderText,
      date,
      customInput,
      onChange,
      dataTestId,
      id,
      withHeader,
      locale = appService.lang || config.defaultLanguage,
      noDatePickerOverflow,
      onCalendarOpen,
      onCalendarClose,
      minDate,
      maxDate,
      customCalendarContainer,
      focusSelectedMonth,
      withKeyboardSupport,
      setValueToNullWhenFormatIsIncorrect,
      onInvalidInputFormat,
      updatePackageAvailability,
      showInModal,
      inline,
      onMonthChange,
      renderDayContents,
      noInitialValueInput,
      showPopperArrow,
      renderCustomHeader
    } = props;

    const reactDatePickerRef = useRef<ReactDatePicker>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      openDatePicker() {
        reactDatePickerRef?.current?.setOpen(true);
      },
      closeDatePicker() {
        reactDatePickerRef?.current?.setOpen(false);
      },
      forceUpdate() {
        reactDatePickerRef?.current?.forceUpdate();
      }
    }));

    const toggleDropdown = useCallback((): void => {
      setIsModalOpen(value => !value);
    }, []);

    const onInputChange = useCallback(
      (newDate: OnChangeDate | null): void => {
        if (setValueToNullWhenFormatIsIncorrect && onInvalidInputFormat && !newDate) {
          onInvalidInputFormat();
        }
        if (!!onChange && newDate) {
          onChange(newDate as OnChangeDateIntersection);
        }
      },
      [setValueToNullWhenFormatIsIncorrect, onChange, onInvalidInputFormat]
    );

    const handleMonthChange = useCallback(
      (onMonthChangeDate: Date): void => {
        if (updatePackageAvailability) {
          updatePackageAvailability(onMonthChangeDate);
        }
        if (onMonthChange) {
          onMonthChange(onMonthChangeDate);
        }
      },
      [updatePackageAvailability, onMonthChange]
    );

    return showInModal ? (
      <DatePickerInModal
        {...props}
        onChange={onChange}
        onInputChange={onInputChange}
        toggleDropdown={toggleDropdown}
        isModalOpen={isModalOpen}
      >
        <ReactDatePickerRenderer
          {...props}
          theme={theme}
          reactDatePickerRef={reactDatePickerRef}
          dataTestId={dataTestId}
          locale={locale}
          id={id}
          calendarContainer={customCalendarContainer}
          withHeader={withHeader}
          renderCustomHeader={renderCustomHeader}
          renderDayContents={renderDayContents}
          showInModal={showInModal}
          toggleDropdown={toggleDropdown}
          onChange={onChange}
          onCalendarOpen={onCalendarOpen}
          onCalendarClose={onCalendarClose}
          customInput={customInput}
          withKeyboardSupport={withKeyboardSupport}
          placeholderText={placeholderText}
          selected={date}
          focusSelectedMonth={focusSelectedMonth}
          minDate={minDate}
          maxDate={maxDate}
          onMonthChange={handleMonthChange}
          onInputChange={onInputChange}
          inline
          noDatePickerOverflow={noDatePickerOverflow}
          noInitialValueInput={noInitialValueInput}
          showPopperArrow={showPopperArrow}
        />
      </DatePickerInModal>
    ) : (
      <ReactDatePickerRenderer
        {...props}
        theme={theme}
        reactDatePickerRef={reactDatePickerRef}
        dataTestId={dataTestId}
        locale={locale}
        calendarContainer={customCalendarContainer}
        withHeader={withHeader}
        renderCustomHeader={renderCustomHeader}
        renderDayContents={renderDayContents}
        showInModal={showInModal}
        toggleDropdown={toggleDropdown}
        onChange={onChange}
        onCalendarOpen={onCalendarOpen}
        onCalendarClose={onCalendarClose}
        customInput={customInput}
        withKeyboardSupport={withKeyboardSupport}
        placeholderText={placeholderText}
        selected={date}
        focusSelectedMonth={focusSelectedMonth}
        minDate={minDate}
        maxDate={maxDate}
        onMonthChange={handleMonthChange}
        onInputChange={onInputChange}
        inline={inline}
        noDatePickerOverflow={noDatePickerOverflow}
        noInitialValueInput={noInitialValueInput}
        showPopperArrow={showPopperArrow}
      />
    );
  }
);

export default DatePicker;
